import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaStar, FaUser, FaMapMarkerAlt, FaCheck, FaCamera, FaImages, FaPaperPlane, FaHeart } from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';

const ReviewModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    reviewer_name: '',
    reviewer_city: '',
    rating: 0,
    liked: true,
    comment: '',
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const MAX_PHOTOS = 3;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const remaining = MAX_PHOTOS - selectedFiles.length;
    const newFiles = files.slice(0, remaining);

    if (newFiles.length === 0) return;

    setSelectedFiles(prev => [...prev, ...newFiles]);

    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const removePhoto = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPhotos = async () => {
    const urls = [];
    for (const file of selectedFiles) {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${ext}`;
      const filePath = `reviews/${fileName}`;

      const { error } = await supabase.storage
        .from('review-photos')
        .upload(filePath, file);

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from('review-photos')
        .getPublicUrl(filePath);

      urls.push(publicUrlData.publicUrl);
    }
    return urls;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.rating === 0) return;
    if (!formData.reviewer_name.trim() || !formData.comment.trim()) return;

    setIsSubmitting(true);

    try {
      let photo_urls = [];
      if (selectedFiles.length > 0) {
        photo_urls = await uploadPhotos();
      }

      const { error } = await supabase.from('reviews').insert([
        {
          reviewer_name: formData.reviewer_name.trim(),
          reviewer_city: formData.reviewer_city.trim() || null,
          rating: formData.rating,
          liked: formData.liked,
          comment: formData.comment.trim(),
          status: 'pending',
          photo_urls: photo_urls.length > 0 ? photo_urls : null,
        },
      ]);

      if (error) throw error;

      setSubmitted(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      reviewer_name: '',
      reviewer_city: '',
      rating: 0,
      liked: true,
      comment: '',
    });
    setHoverRating(0);
    setSubmitted(false);
    setSelectedFiles([]);
    setPreviews([]);
    onClose();
  };

  if (!isMounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-white shadow-2xl flex flex-col overflow-hidden relative w-screen h-screen rounded-none max-w-none max-h-none sm:rounded-xl sm:max-w-lg sm:w-full sm:max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 sm:p-6 text-center border-b border-gray-200 sticky top-0 bg-green-50 z-10 sm:rounded-t-xl">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full text-gray-500 hover:text-gray-800 transition-colors"
              >
                <FaTimes size={16} />
              </button>
              <h3 className="text-xl sm:text-2xl font-playfair font-bold text-gray-900 mb-1">
                Comparte tu Experiencia
              </h3>
              <p className="text-gray-500 text-sm">Tu opinión nos ayuda a mejorar</p>
            </div>

            {/* Main Content */}
            <div className="flex-grow overflow-y-auto">
              {submitted ? (
                <div className="p-8 sm:p-10 text-center flex flex-col justify-center items-center h-full">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6 shadow-lg"
                  >
                    <FaCheck className="text-white" size={28} />
                  </motion.div>
                  <h4 className="text-xl sm:text-2xl font-playfair font-bold text-gray-900 mb-2 sm:mb-3">
                    ¡Gracias por tu reseña!
                  </h4>
                  <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto mb-6 sm:mb-8">
                    Tu experiencia será revisada y publicada pronto. Nos encanta saber que disfrutaste tu estadía.
                  </p>
                  <button
                    onClick={handleClose}
                    className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
                  >
                    Cerrar
                  </button>
                </div>
              ) : (
                <>
                  {/* Rating Section */}
                  <div className="px-6 py-4 sm:py-5 border-b border-gray-100 text-center bg-gray-50">
                    <p className="text-sm font-medium text-gray-700 mb-3">¿Cómo fue tu estadía?</p>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                          className="p-1 transition-all hover:scale-125"
                        >
                          <FaStar
                            size={32}
                            className={`transition-colors ${
                              (hoverRating || formData.rating) >= star
                                ? 'text-yellow-400 drop-shadow-sm'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    {formData.rating > 0 && (
                      <motion.p
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm font-semibold mt-2 text-green-600"
                      >
                        {formData.rating === 1 && 'Malo'}
                        {formData.rating === 2 && 'Regular'}
                        {formData.rating === 3 && 'Bueno'}
                        {formData.rating === 4 && 'Muy bueno'}
                        {formData.rating === 5 && 'Excelente'}
                      </motion.p>
                    )}
                  </div>

                  <form onSubmit={handleSubmit} id="review-form" className="p-4 sm:p-6 space-y-4">
                    {/* Name + City Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Nombre *</label>
                        <div className="relative">
                          <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                          <input
                            type="text"
                            name="reviewer_name"
                            value={formData.reviewer_name}
                            onChange={handleChange}
                            required
                            placeholder="Tu nombre"
                            className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white outline-none transition-all text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Ciudad</label>
                        <div className="relative">
                          <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                          <input
                            type="text"
                            name="reviewer_city"
                            value={formData.reviewer_city}
                            onChange={handleChange}
                            placeholder="¿De dónde nos visitas?"
                            className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white outline-none transition-all text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Liked */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">¿Lo recomendarías?</label>
                      <div className="flex gap-3">
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, liked: true }))} className={`flex-1 py-2.5 rounded-xl border-2 font-medium text-sm transition-all flex items-center justify-center gap-2 ${formData.liked ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                          <FaHeart size={12} /> Sí, totalmente
                        </button>
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, liked: false }))} className={`flex-1 py-2.5 rounded-xl border-2 font-medium text-sm transition-all ${!formData.liked ? 'border-red-400 bg-red-50 text-red-600' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                          No
                        </button>
                      </div>
                    </div>
                    {/* Comment */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Tu experiencia *</label>
                      <textarea
                        name="comment"
                        value={formData.comment}
                        onChange={handleChange}
                        required
                        rows={4}
                        placeholder="¿Qué fue lo que más disfrutaste de tu estadía?"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white outline-none transition-all text-sm resize-none"
                      />
                    </div>
                    {/* Photos */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fotos</label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {previews.length === 0 ? (
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full py-4 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center gap-3 text-gray-400 hover:border-green-400 hover:text-green-500 hover:bg-green-50 transition-all">
                          <FaImages size={20} />
                          <span className="text-sm font-medium">Agregar fotos (máx. {MAX_PHOTOS})</span>
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          {previews.map((src, i) => (
                            <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-gray-100 group">
                              <img src={src} alt={`Preview ${i+1}`} className="w-full h-full object-cover" />
                              <button type="button" onClick={() => removePhoto(i)} className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <FaTimes className="text-white" size={14} />
                              </button>
                            </div>
                          ))}
                          {selectedFiles.length < MAX_PHOTOS && (
                            <button type="button" onClick={() => fileInputRef.current?.click()} className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 hover:border-green-400 hover:text-green-500 transition-colors">
                              <FaCamera size={16} />
                              <span className="text-[10px] mt-1 font-medium">Agregar</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </form>
                </>
              )}
            </div>

            {/* Themed Footer */}
            {!submitted && (
              <div className="p-5 sm:p-6 bg-green-50 border-t border-gray-200 sm:rounded-b-xl">
                <button
                  type="submit"
                  form="review-form"
                  disabled={isSubmitting || formData.rating === 0}
                  className="w-full py-3.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane size={14} />
                      Enviar Reseña
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  , document.body);
};

export default ReviewModal;